import {useContext} from "react";
import Moment from "moment/moment";
import "moment-timezone";
import AuthContext from "context/AuthContext";

/**
 * Hook para manejo de fechas con zona horaria de la organización
 * @returns {Object} Utilidades para trabajar con fechas
 */
export const MomentUtils = () => {
    const {org} = useContext(AuthContext);

    // Timezone de la organización
    const timezone = org?.timezone || "Europe/Madrid";

    /**
     * Convierte fecha UTC a zona horaria de la organización
     * @param {string|Date} date - Fecha en UTC
     * @returns {Moment} Objeto moment en zona horaria local
     */
    const momentTz = (date) => {
        if (!date) return null;
        return Moment.utc(date).tz(timezone);
    };

    /**
     * Formatea fecha UTC a string en zona horaria local
     * @param {string|Date} date - Fecha en UTC
     * @param {string} format - Formato de salida
     * @returns {string} Fecha formateada
     */
    const formatDate = (date, format = "DD/MM/YYYY HH:mm:ss") => {
        if (!date) return "";
        return momentTz(date).format(format);
    };

    /**
     * Formatea una fecha LOCAL (no UTC) a string en zona horaria de la organización.
     * Usar para fechas ya en hora local (como las del date picker).
     */
    const formatLocalDate = (date, format = "DD/MM/YYYY HH:mm:ss") => {
        if (!date) return "";
        return Moment.tz(date, timezone).format(format);
    };

    /**
     * Dado un rango de fechas (en hora local), devuelve el rango ajustado a 00:00:00 - 23:59:59
     * en hora local y convertido a UTC para enviar al backend.
     */
    const getDayRangeUTC = (startDate, endDate) => {
        const tz = timezone;

        // Interpretar las fechas como si fueran locales en la zona de la organización
        const localStart = Moment.tz(Moment(startDate).format("YYYY-MM-DD"), "YYYY-MM-DD", tz).startOf("day"); // 00:00:00 en hora local

        const localEnd = Moment.tz(Moment(endDate).format("YYYY-MM-DD"), "YYYY-MM-DD", tz).endOf("day"); // 23:59:59 en hora local

        return {
            localStart: localStart.toDate(),
            localEnd: localEnd.toDate(),
            start: localStart.utc().toISOString(), // → UTC para backend
            end: localEnd.utc().toISOString(), // → UTC para backend
        };
    };

    const now = () => {
        return Moment().tz(timezone);
    };

    return {
        momentTz,
        formatDate,
        formatLocalDate,
        getDayRangeUTC,
        now,
        timezone: timezone,
    };
};

export default MomentUtils;
